/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from 'react';
import '../../../../stylesheets/stylesLayout/yourProperties.scss';
import Card from '../../../../muiComponents/Card';
import GridChild from '../../../../muiComponents/GridChild';
import Grid from '../../../../muiComponents/Grid';
import {
    singleColumnGrid,
    fullColumnGrid,
    doubleColumnGrid,
} from '../../../../utils/property/propertyUtils';
import TextInput from '../../../../muiComponents/TextInput/TextInputV2';
import {
    usePropertyFetching,
    usePropertyRoomDetail,
    usePropertyValidation,
} from '../../../contextsProperty/ContextProperties';
import {
    Alert,
    AlertTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import Tag from '../../../../muiComponents/Tag';
import useTranslate from '../../../../customHooks/useTranslate';
import Button from '../../../../muiComponents/Button';
import { PROGRESS_ROOM_CONFIGURATION_TAB } from '../../../../types/properties/propertyTypes';
import LoadingTab from '../LoadingTab';
import Title from '../Title';
import {
    gridSpacing20,
    gridSpacing30,
} from '../../../../utils/commonVariables/grid';

export default function Occupancy() {
    const translate = useTranslate();
    const {
        adultsOnly,
        occupancy,
        standardOccupancy,
        minAdults,
        maxAdults,
        minChildren,
        maxChildren,
        handleRoomInputChange,
    } = usePropertyRoomDetail();
    const { handleIsValidated, errors, isValidated, handleValidate } =
        usePropertyValidation();
    const { isFetching } = usePropertyFetching();
    const pad = n => n.toString().padStart(2, '0');

    const occupancyArray = useMemo(
        () =>
            occupancy
                .split('|')
                .map(item =>
                    item.split('.').map(stringNum => parseInt(stringNum))
                ),
        [occupancy]
    );

    const arrayAdults = useMemo(
        () =>
            Array.from(
                { length: maxAdults - minAdults + 1 },
                (_, i) => i + minAdults
            ),
        [minAdults, maxAdults]
    );

    const arrayChildren = useMemo(
        () =>
            Array.from(
                { length: maxChildren - minChildren + 1 },
                (_, i) => i + minChildren
            ),
        [maxChildren, minChildren]
    );

    const toggleOccupancy = (adults, children) => {
        const tagString = `${pad(adults)}.${pad(children)}`;

        let current = occupancy ? occupancy.split('|').filter(Boolean) : [];

        const exists = current.includes(tagString);

        let updated = exists
            ? current.filter(item => item !== tagString)
            : [...current, tagString];

        updated.sort((a, b) => {
            const [aAdults, aChildren] = a.split('.').map(Number);
            const [bAdults, bChildren] = b.split('.').map(Number);
            return aAdults !== bAdults
                ? aAdults - bAdults
                : aChildren - bChildren;
        });

        handleRoomInputChange('occupancy', updated.join('|'));
    };

    const selectAll = () => {
        const allCombinations = [];

        for (let adult of arrayAdults) {
            for (let child of arrayChildren) {
                allCombinations.push(`${pad(adult)}.${pad(child)}`);
            }
        }

        allCombinations.sort((a, b) => {
            const [aAdults, aChildren] = a.split('.').map(Number);
            const [bAdults, bChildren] = b.split('.').map(Number);
            return aAdults !== bAdults
                ? aAdults - bAdults
                : aChildren - bChildren;
        });

        handleRoomInputChange('occupancy', allCombinations.join('|'));
    };

    const isOccupancyTableHidden = useMemo(() => {
        return !standardOccupancy || !minAdults || !maxAdults;
    }, [standardOccupancy, minAdults, maxAdults]);

    useEffect(() => {
        if (adultsOnly && (minChildren || maxChildren)) {
            handleRoomInputChange('minChildren', 0);
            handleRoomInputChange('maxChildren', 0);
        }

        if (isValidated) {
            handleValidate(PROGRESS_ROOM_CONFIGURATION_TAB.OCCUPANCY);
        }
    }, [adultsOnly, minChildren, maxChildren]);

    const handleBlur = () => {
        if (isValidated) {
            handleValidate(PROGRESS_ROOM_CONFIGURATION_TAB.OCCUPANCY);
        }
    };

    useEffect(() => {
        const filteredArray = occupancyArray.filter(
            ([adultSel, childrenSel]) => {
                const adultsValid =
                    adultSel >= minAdults && adultSel <= maxAdults;
                const childrenValid = adultsOnly
                    ? childrenSel === 0 // si es solo para adultos, no se permiten niños
                    : childrenSel >= minChildren && childrenSel <= maxChildren;

                return adultsValid && childrenValid;
            }
        );

        if (filteredArray.length !== occupancyArray.length) {
            const formatted = filteredArray
                .map(
                    ([a, c]) =>
                        `${String(a).padStart(2, '0')}.${String(c).padStart(
                            2,
                            '0'
                        )}`
                )
                .join('|');

            handleRoomInputChange('occupancy', formatted);
        }
    }, [
        adultsOnly,
        standardOccupancy,
        maxAdults,
        minAdults,
        minChildren,
        maxChildren,
    ]);

    useEffect(() => {
        return () => handleIsValidated(false);
    }, []);

    if (isFetching) {
        return <LoadingTab />;
    }

    return (
        <div className='property-page__content flex-column gap-20'>
            <div>
                <Title
                    as='h1'
                    text={'occupancy_section'}
                    className='text-align-center'
                />
            </div>
            <Card className='padding30'>
                <Grid
                    columnSpacing={gridSpacing30}
                    rowSpacing={gridSpacing20}
                    style={{ width: 'unset' }}
                >
                    <GridChild gridProps={fullColumnGrid}>
                        <TextInput
                            labelText='base_occupancy'
                            handleChange={e => {
                                handleRoomInputChange(
                                    'standardOccupancy',
                                    parseInt(e.target.value)
                                );
                            }}
                            value={standardOccupancy}
                            name='baseOccupancy'
                            type='number'
                            variant='outlined'
                            onBlur={handleBlur}
                            error={!!errors.standardOccupancy}
                            errorMessage={errors.standardOccupancy}
                        />
                    </GridChild>
                    <GridChild
                        gridProps={
                            adultsOnly ? doubleColumnGrid : singleColumnGrid
                        }
                    >
                        <TextInput
                            labelText='min_adults'
                            handleChange={e => {
                                handleRoomInputChange(
                                    'minAdults',
                                    parseInt(e.target.value)
                                );
                            }}
                            value={minAdults}
                            name='minAdults'
                            type='number'
                            variant='outlined'
                            onBlur={handleBlur}
                            error={!!errors.minAdults}
                            errorMessage={errors.minAdults}
                        />
                    </GridChild>
                    <GridChild
                        gridProps={
                            adultsOnly ? doubleColumnGrid : singleColumnGrid
                        }
                    >
                        <TextInput
                            labelText='max_adults'
                            handleChange={e => {
                                handleRoomInputChange(
                                    'maxAdults',
                                    parseInt(e.target.value)
                                );
                            }}
                            value={maxAdults}
                            name='maxAdults'
                            type='number'
                            variant='outlined'
                            onBlur={handleBlur}
                            error={!!errors.maxAdults}
                            errorMessage={errors.maxAdults}
                        />
                    </GridChild>
                    {!adultsOnly && (
                        <GridChild gridProps={singleColumnGrid}>
                            <TextInput
                                labelText='min_child'
                                handleChange={e => {
                                    handleRoomInputChange(
                                        'minChildren',
                                        parseInt(e.target.value)
                                    );
                                }}
                                value={minChildren}
                                name='minChildren'
                                type='number'
                                variant='outlined'
                            />
                        </GridChild>
                    )}
                    {!adultsOnly && (
                        <GridChild gridProps={singleColumnGrid}>
                            <TextInput
                                labelText='max_child'
                                handleChange={e => {
                                    handleRoomInputChange(
                                        'maxChildren',
                                        parseInt(e.target.value)
                                    );
                                }}
                                value={maxChildren}
                                name='maxChildren'
                                type='number'
                                variant='outlined'
                                onBlur={handleBlur}
                                error={!!errors.maxChildren}
                                errorMessage={errors.maxChildren}
                            />
                        </GridChild>
                    )}
                </Grid>
            </Card>
            {isOccupancyTableHidden ? (
                <Card className='padding20'>
                    <Alert severity='info'>
                        <AlertTitle sx={{ fontWeight: 'bold' }}>
                            {translate('occupancy_title')}
                        </AlertTitle>
                        <p style={{ fontWeight: 'normal' }}>
                            {translate('occupancy_subtitle')}
                        </p>
                    </Alert>
                </Card>
            ) : (
                <Card className='padding-inline30 '>
                    <div className='overflow-x-auto'>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell></TableCell>
                                    {arrayAdults.map(adult => {
                                        const adultText =
                                            adult === 1 ? 'adult' : 'Adults';
                                        return (
                                            <TableCell
                                                key={adult}
                                                className='text-align-center'
                                            >{`${adult} ${translate(
                                                adultText
                                            )}`}</TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {arrayChildren.map(child => {
                                    const childText =
                                        child === 1 ? 'child' : 'Children';
                                    return (
                                        <TableRow key={`row-${child}`}>
                                            <TableCell className=' white-space-nowrap'>{`${child} ${translate(
                                                childText
                                            )}`}</TableCell>
                                            {arrayAdults.map(adult => {
                                                const isSelected =
                                                    occupancyArray.some(
                                                        ([
                                                            adultSel,
                                                            childrenSel,
                                                        ]) =>
                                                            adultSel ===
                                                                adult &&
                                                            childrenSel ===
                                                                child
                                                    );
                                                return (
                                                    <TableCell
                                                        key={`cell-${adult}-${child}`}
                                                    >
                                                        <span className='flex flex-center'>
                                                            <Tag
                                                                button
                                                                onClick={() => {
                                                                    toggleOccupancy(
                                                                        adult,
                                                                        child
                                                                    );
                                                                }}
                                                                className='padding-inline50 border-radius16'
                                                                size='large'
                                                                color='cyan'
                                                                variant={
                                                                    isSelected
                                                                        ? 'contrast'
                                                                        : 'surface'
                                                                }
                                                                text={`${adult}.${child}`}
                                                            />
                                                        </span>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                    <div className='flex flex-between padding-block10'>
                        <Button
                            variant='outlined'
                            onClick={() =>
                                handleRoomInputChange('occupancy', '')
                            }
                        >
                            {translate('deselect_all')}
                        </Button>
                        <Button variant='contained' onClick={selectAll}>
                            {translate('select_all')}
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
}
